import React, { useEffect } from 'react';
import { datePrefillFor, noteReportPopupOpened } from '../lib/lastGeneratedDates';
import ReportGenerationPopup from './ReportGenerationPopup';
import FixedIncomeDistributionPopup from './FixedIncomeDistributionPopup';
import FixedIncomeHoldingsByMaturityPopup from './FixedIncomeHoldingsByMaturityPopup';
import PerformanceSummaryCompoundPopup from './PerformanceSummaryCompoundPopup';
import BondTransactionsPopup from './BondTransactionsPopup';
import RealizedGainsLossesPopup from './RealizedGainsLossesPopup';
import UnrealizedGainsLossesPopup from './UnrealizedGainsLossesPopup';
import DateToDateGainLossPopup from './DateToDateGainLossPopup';
import IncomeAndExpensesPopup from './IncomeAndExpensesPopup';
import CashLedgerRunningBalancePopup from './CashLedgerRunningBalancePopup';
import TransactionSummaryPopup from './TransactionSummaryPopup';
import SignificantContributionsWithdrawalsPopup from './SignificantContributionsWithdrawalsPopup';
import FixedIncomeAmortizationAccretionPopup from './FixedIncomeAmortizationAccretionPopup';
import FixedIncomeCashProjectionPopup from './FixedIncomeCashProjectionPopup';
import AssetsUnderManagementPopup from './AssetsUnderManagementPopup';
import AssetsUnderManagementWithPortCodePopup from './AssetsUnderManagementWithPortCodePopup';
import BillingPopup from './BillingPopup';
import PortfolioOverviewPopup from './PortfolioOverviewPopup';
import PortfolioPerformanceSummaryPopup from './PortfolioPerformanceSummaryPopup';
import TradeBlotterPopup from './TradeBlotterPopup';
import TransferTransactionsPopup from './TransferTransactionsPopup';
import SecurityCrossReferencePopup from './SecurityCrossReferencePopup';
import PerformanceByAssetClassPopup from './PerformanceByAssetClassPopup';
import PerformanceBySecurityPopup from './PerformanceBySecurityPopup';
import IndividualAccountSummaryPopup from './IndividualAccountSummaryPopup';
import PortfolioPerformanceCrossReferencePopup from './PortfolioPerformanceCrossReferencePopup';
import PerformanceHistoryPopup from './PerformanceHistoryPopup';
import PerformanceHistoryByAssetClassPopup from './PerformanceHistoryByAssetClassPopup';
import PerformanceHistoryBySectorPopup from './PerformanceHistoryBySectorPopup';
import PerformanceHistoryDetailPopup from './PerformanceHistoryDetailPopup';
import AssetReconciliationPopup from './AssetReconciliationPopup';
import PerformanceReconciliationPopup from './PerformanceReconciliationPopup';
import ACBProofPopup from './ACBProofPopup';
import HistoricalMarketValuePopup from './HistoricalMarketValuePopup';

/**
 * The report-type -> configuration-dialog router.
 *
 * Extracted from ReportsPage so the report window can reopen the SAME dialog
 * the Reports page uses when the PM reruns a report with different parameters
 * (see windows/ReportPopup.jsx). One switch, two consumers - a dialog added for
 * a new report type reaches both surfaces at once.
 *
 * Every dialog is driven from one `onConfirm(params)` prop: report-specific
 * dialogs take `onConfirm`, while the generic fallback takes `onGenerate`, and
 * this component adapts the two.
 *
 * `initialParams` is an EXPLICIT full restore: every dialog opens on those exact
 * values instead of its blank defaults (see lib/reportPrefill.js). Callers that
 * pass nothing get the date-only carry-over instead - the date of the last
 * generation of THIS report type, if that is what was last generated (see
 * lib/lastGeneratedDates.js) - and blank defaults for everything else.
 */
const ReportConfigDialog = ({
  reportType,
  reportName,
  reportDescription = '',
  isOpen,
  onClose,
  onConfirm,
  prefillClientName = null,
  initialParams = null,
  darkMode = false,
}) => {
  // Opening a report other than the last generated one wipes the remembered
  // date. Declared above the early return so the hook stays unconditional.
  useEffect(() => {
    if (isOpen) noteReportPopupOpened(reportType);
  }, [isOpen, reportType]);

  if (!isOpen) return null;

  const popupProps = {
    isOpen,
    onClose,
    onConfirm,
    onCancel: onClose,
    initialParams: initialParams || datePrefillFor(reportType),
    reportInfo: { name: reportName, description: reportDescription },
    darkMode,
  };

  // Several report types share one dialog component (transaction_history /
  // transaction_summary / master_transaction_summary, purchase_and_sale /
  // trade_blotter, ...). Keying the routed dialog on the type remounts it when
  // the type changes, so a shared component can never open on the previous
  // type's field state.
  switch (reportType) {
    case 'fixed_income_distribution':
      return <FixedIncomeDistributionPopup key={reportType} {...popupProps} />;
    case 'fixed_income_holdings_by_maturity':
      return <FixedIncomeHoldingsByMaturityPopup key={reportType} {...popupProps} />;
    case 'performance_summary_compound':
      return <PerformanceSummaryCompoundPopup key={reportType} {...popupProps} />;
    case 'bond_transactions':
      return <BondTransactionsPopup key={reportType} {...popupProps} />;
    case 'realized_gains_losses':
      return <RealizedGainsLossesPopup key={reportType} {...popupProps} />;
    case 'unrealized_gains_losses':
      return <UnrealizedGainsLossesPopup key={reportType} {...popupProps} />;
    case 'date_to_date_gain_loss':
    case 'change_in_portfolio_value':
      return <DateToDateGainLossPopup key={reportType} {...popupProps} />;
    case 'income_and_expenses':
      return <IncomeAndExpensesPopup key={reportType} {...popupProps} />;
    case 'cash_ledger_running_balance':
      return <CashLedgerRunningBalancePopup key={reportType} {...popupProps} />;
    case 'transaction_history':
    case 'transaction_summary':
    case 'master_transaction_summary':
      return <TransactionSummaryPopup key={reportType} {...popupProps} />;
    case 'significant_contributions_withdrawals':
      return <SignificantContributionsWithdrawalsPopup key={reportType} {...popupProps} />;
    case 'fixed_income_amortization_accretion':
      return <FixedIncomeAmortizationAccretionPopup key={reportType} {...popupProps} />;
    case 'fixed_income_cash_projection':
      return <FixedIncomeCashProjectionPopup key={reportType} {...popupProps} />;
    case 'assets_under_management':
      return <AssetsUnderManagementPopup key={reportType} {...popupProps} />;
    case 'assets_under_management_with_port_code':
      return <AssetsUnderManagementWithPortCodePopup key={reportType} {...popupProps} />;
    case 'billing':
      return <BillingPopup key={reportType} {...popupProps} />;
    case 'portfolio_summary':
      return <PortfolioOverviewPopup key={reportType} {...popupProps} />;
    case 'portfolio_performance_summary':
      return <PortfolioPerformanceSummaryPopup key={reportType} {...popupProps} />;
    case 'purchase_and_sale':
    case 'trade_blotter':
      return <TradeBlotterPopup key={reportType} {...popupProps} />;
    // funds_in_and_out shares this dialog: same field set (portfolio + date
    // range + currency) and the heading comes from reportInfo.name, so it
    // does not need a dedicated popup component.
    case 'transfer_transactions':
    case 'funds_in_and_out':
      return <TransferTransactionsPopup key={reportType} {...popupProps} />;
    case 'security_cross_reference':
      return <SecurityCrossReferencePopup key={reportType} {...popupProps} />;
    case 'performance_by_asset_class':
      return <PerformanceByAssetClassPopup key={reportType} {...popupProps} />;
    case 'performance_by_security':
      return <PerformanceBySecurityPopup key={reportType} {...popupProps} />;
    case 'individual_account_summary':
      return <IndividualAccountSummaryPopup key={reportType} {...popupProps} />;
    case 'portfolio_performance_cross_reference':
      return <PortfolioPerformanceCrossReferencePopup key={reportType} {...popupProps} />;
    case 'performance_history':
      return <PerformanceHistoryPopup key={reportType} {...popupProps} />;
    case 'performance_history_by_asset_class':
      return <PerformanceHistoryByAssetClassPopup key={reportType} {...popupProps} />;
    case 'performance_history_by_sector':
      return <PerformanceHistoryBySectorPopup key={reportType} {...popupProps} />;
    case 'performance_history_detail':
      return <PerformanceHistoryDetailPopup key={reportType} {...popupProps} />;
    case 'asset_reconciliation':
      return <AssetReconciliationPopup key={reportType} {...popupProps} />;
    case 'performance_reconciliation':
      return <PerformanceReconciliationPopup key={reportType} {...popupProps} />;
    case 'acb_proof':
      return <ACBProofPopup key={reportType} {...popupProps} />;
    case 'historical_market_value':
      return <HistoricalMarketValuePopup key={reportType} {...popupProps} />;
    default:
      return (
        <ReportGenerationPopup
          key={reportType}
          isOpen={isOpen}
          onClose={onClose}
          onGenerate={onConfirm}
          reportType={reportType}
          reportName={reportName}
          prefillClientName={prefillClientName}
          initialParams={popupProps.initialParams}
          darkMode={darkMode}
        />
      );
  }
};

export default ReportConfigDialog;
